import React, { useMemo, createContext, useContext } from 'react';
import {
  TelemetryPanelConfig,
  TelemetryLayoutMode,
  TelemetryUnitSystem,
  TelemetryViewportState,
} from '../types/telemetry-dashboard.types';
import { useTelemetryLayout } from '../hooks/useTelemetryLayout';
import { useTelemetryEChartsSync } from '../hooks/useTelemetryEChartsSync';
import { findTelemetryGapZones, findTelemetryEmptySlots, TELEMETRY_GRID_PAD, TelemetryGapZone, TelemetryEmptySlot } from '../utils/telemetry-grid.utils';
import { TelemetryGapZoneUI } from './TelemetryGapZone';
import { TelemetryEmptySlotUI } from './TelemetryEmptySlot';
import '../styles/telemetry-dashboard-grid.css';

export interface TelemetryLayoutContextValue {
  colW: number;
  pxX: (x: number) => number;
  pxY: (y: number) => number;
  pxW: (w: number) => number;
  pxH: (h: number) => number;
  dragId: string | null;
  dragPos: { top: number; left: number } | null;
  resizeId: string | null;
  resizePos: { left: number; top: number; width: number; height: number } | null;
  gridScrollRef: React.RefObject<HTMLDivElement>;
  gridElRef: React.RefObject<HTMLDivElement>;
  onDragDown: (id: string, e: React.PointerEvent) => void;
  onResizeDown: (id: string, dir: string, e: React.PointerEvent) => void;
  layoutMode: TelemetryLayoutMode;

  // Ambient context values for child cards & charts
  timeframe: number;
  customStartTimeSec?: number | null;
  precision: number;
  units: TelemetryUnitSystem | any;
  viewport: TelemetryViewportState;
  onViewportChange?: (patch: Partial<TelemetryViewportState>) => void;
  viewportLabel: string;
  isPaused: boolean;
  isAtStartBoundary: boolean;
  isLoadingHistory: boolean;
  isTelemetryDataLoading: boolean;
  onJumpLive?: () => void;
  getChartWrapRef?: (id: string) => (el: HTMLElement | null) => void;
  patchWidget?: (id: string, patch: Partial<any>) => void;
  activeMenuTriggerId?: string | null;
  telemetrySeries?: any[];
}

export const TelemetryLayoutContext = createContext<TelemetryLayoutContextValue | null>(null);

export const useTelemetryLayoutContext = () => {
  const ctx = useContext(TelemetryLayoutContext);
  if (!ctx) {
    throw new Error('Telemetry compound components must be used within <TelemetryLayout>');
  }
  return ctx;
};

export interface TelemetryLayoutProps {
  layout: TelemetryPanelConfig[] | any[];
  setLayout?: (layout: any[]) => void;
  onLayoutChange?: (layout: any[]) => void;
  layoutMode?: TelemetryLayoutMode;
  timeframe?: number;
  customStartTimeSec?: number | null;
  precision?: number;
  units?: TelemetryUnitSystem | any;
  viewport?: TelemetryViewportState | any;
  viewportLabel?: string;
  isPaused?: boolean;
  isAtStartBoundary?: boolean;
  isLoadingHistory?: boolean;
  isTelemetryDataLoading?: boolean;
  onJumpLive?: () => void;
  getChartWrapRef?: (id: string) => (el: HTMLElement | null) => void;
  patchWidget?: (id: string, patch: Partial<any>) => void;
  activeMenuTriggerId?: string | null;
  children?: React.ReactNode | ((layoutCtx: TelemetryLayoutContextValue) => React.ReactNode);
  onGapInsert?: (gap: TelemetryGapZone) => void;
  onOpenAdd?: () => void;
  telemetrySeries?: any[];
  onViewportChange?: (patch: Partial<TelemetryViewportState>) => void;
  className?: string;
  style?: React.CSSProperties;
}

export const TelemetryLayout: React.FC<TelemetryLayoutProps> = ({
  layout,
  setLayout,
  onLayoutChange,
  layoutMode = 'grid',
  timeframe = 5,
  customStartTimeSec = null,
  precision = 2,
  units = {},
  viewport = { spanMs: 300000, offsetMs: 0 },
  viewportLabel = '5 min window · live',
  isPaused = false,
  isAtStartBoundary = false,
  isLoadingHistory = false,
  isTelemetryDataLoading = false,
  onJumpLive,
  getChartWrapRef,
  patchWidget,
  activeMenuTriggerId,
  children,
  onGapInsert,
  onOpenAdd,
  telemetrySeries = [],
  onViewportChange,
  className = '',
  style,
}) => {
  const updateLayout = onLayoutChange || setLayout || (() => { });

  const layoutState = useTelemetryLayout(layout, updateLayout, layoutMode);

  const {
    colW,
    pxY,
    gridScrollRef,
    gridElRef,
  } = layoutState;

  // Synchronize ECharts canvas rendering
  useTelemetryEChartsSync(
    layout,
    timeframe,
    precision,
    units,
    viewport,
    gridElRef as any,
    telemetrySeries,
    onViewportChange
  );

  // Calculate gaps
  const gaps = useMemo(() => findTelemetryGapZones(layout as any, colW), [layout, colW]);

  // Calculate vacant grid slots
  const emptySlots = useMemo(
    () => (layoutMode === 'grid' ? findTelemetryEmptySlots(layout as any, colW) : []),
    [layout, colW, layoutMode]
  );

  // Max grid height for scroll container
  const maxY = useMemo(
    () => layout.reduce((m, p) => Math.max(m, p.y + p.h), 0),
    [layout]
  );

  const contextValue: TelemetryLayoutContextValue = useMemo(
    () => ({
      ...layoutState,
      timeframe,
      customStartTimeSec,
      precision,
      units,
      viewport,
      onViewportChange,
      viewportLabel,
      isPaused,
      isAtStartBoundary,
      isLoadingHistory,
      isTelemetryDataLoading,
      onJumpLive,
      getChartWrapRef,
      patchWidget,
      activeMenuTriggerId,
      telemetrySeries,
    }),
    [
      layoutState,
      timeframe,
      customStartTimeSec,
      precision,
      units,
      viewport,
      onViewportChange,
      viewportLabel,
      isPaused,
      isAtStartBoundary,
      isLoadingHistory,
      isTelemetryDataLoading,
      onJumpLive,
      getChartWrapRef,
      patchWidget,
      activeMenuTriggerId,
      telemetrySeries,
    ]
  );

  return (
    <TelemetryLayoutContext.Provider value={contextValue}>
      <div
        ref={gridScrollRef}
        className={`dashboard-viewport td-grid-viewport telemetry-dashboard-viewport telemetry-viewport--${layoutMode} ${className}`}
        style={style}
      >
        <div
          ref={gridElRef}
          className={`td-grid-container telemetry-dashboard-container-wrapper telemetry-container--${layoutMode}`}
          style={
            layoutMode === 'grid'
              ? { minHeight: Math.max(420, pxY(maxY) + TELEMETRY_GRID_PAD) }
              : undefined
          }
        >
          {/* Blank Workspace Empty State */}
          {layout.length === 0 && (
            <div className="td-grid-empty-wrap">
              <div className="td-grid-empty-card">
                <div className="td-grid-empty-icon">
                  <svg width="26" height="26" viewBox="0 0 16 16" fill="none">
                    <rect x="2" y="2" width="5" height="5" rx="1" stroke="var(--accent)" strokeWidth="1.2" />
                    <rect x="9" y="2" width="5" height="5" rx="1" stroke="var(--accent)" strokeWidth="1.2" />
                    <rect x="2" y="9" width="5" height="5" rx="1" stroke="var(--accent)" strokeWidth="1.2" />
                    <rect x="9" y="9" width="5" height="5" rx="1" stroke="var(--accent)" strokeWidth="1.2" />
                  </svg>
                </div>
                <h3 className="td-grid-empty-title">Blank workspace</h3>
                <p className="td-grid-empty-desc">
                  Build a monitoring view for this mission. Add telemetry panels and arrange them to suit your operation.
                </p>
                {onOpenAdd && (
                  <button onClick={onOpenAdd} className="btn btn-md btn-solid td-grid-empty-btn">
                    Add your first panel
                  </button>
                )}
              </div>
            </div>
          )}

          {/* Vacant Slot Placeholders (Grid Mode) */}
          {layoutMode === 'grid' && onGapInsert &&
            emptySlots.map((slot) => (
              <TelemetryEmptySlotUI
                key={slot.key}
                slot={slot}
                onInsert={(s) =>
                  onGapInsert({
                    key: s.key,
                    orient: 'h',
                    isSlot: true,
                    x: s.x,
                    y: s.y,
                    xStart: s.x,
                    xEnd: s.x + s.w,
                    yStart: s.y,
                    yEnd: s.y + s.h,
                    panelW: s.w,
                    panelH: s.h,
                    left: s.left,
                    top: s.top,
                    width: s.width,
                    height: s.height,
                  })
                }
              />
            ))}

          {/* Gap Insertion Zones (Grid Mode) */}
          {layoutMode === 'grid' && onGapInsert &&
            gaps.map((gap) => (
              <TelemetryGapZoneUI
                key={gap.key}
                gap={gap}
                mode="grid"
                onInsert={onGapInsert}
              />
            ))}

          {typeof children === 'function' ? children(contextValue) : children}
        </div>
      </div>
    </TelemetryLayoutContext.Provider>
  );
};

// Re-export aliases for backward compatibility
export { TelemetryLayout as TelemetryGrid, TelemetryLayout as TelemetryDashboardGrid };
